import classNames from 'classnames/bind';
import styles from './LearningActivity.module.css';
import Tabs from '../../../components/Tabs';
import {
  MAX_ACTIVITY_X_AXIS,
  ALL,
  CASES,
  CONTENTS_LEARNING_ACTIVITIES,
  CONTENT_LEARNING,
  DRAW_LIST_CONTENT_LABELS,
  STICKER,
  LEARNING_TOOLS_ACTIVITIES,
  LEARNING_TOOL_ACTIVITY,
  QUIZ_LEARNING,
  SHOW_DETAILS_MESSAGE,
  PAGE,
} from '../../../constants';
import { useEffect, useState } from 'react';
import Select from '../../../components/Select';
import AnimatedBar from '../../../components/AnimatedBar';
import {
  ActivityDetail,
  Annotation,
  ChartList,
  ContentsActivity,
  Quiz,
  QuizActivity,
  QuizChartList,
} from '../../../types/analysis';
import TooltipWrapper from '../../../components/Tooltip/TooltipWrapper';
import { isMobile } from '../../../utils/util';
import { analysisApiSlice } from '../../../services/analysis';
import QuizLearning from '../QuizLearning';
import TotalCorrectRate from '../TotalCorrectRate';
import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import {
  myProjectActivityListChanged,
  quizListChanged,
  quizListPageChanged,
  quizTotalCorrectChanged,
  selectMyProjectActivityList,
  selectProjectIndexList,
  selectStudentActivityList,
  studentProjectActivityListChanged,
} from '../../../features/analysisSlice';
import { openSelpubCloud } from '../../../utils/selpub';

interface LearningActivity {
  projectId: number;
  userId?: number;
  tabIdx?: number;
}

interface GraphItemProps {
  rate: string;
  idx: number;
  isAverage?: boolean;
}

const CONTENTS_WITH_DETAILS = ['classtalkCount'];
const TOOLS_WITH_DETAILS = [
  'bookMarkCount',
  'highlighterCount',
  'memoCount',
  'drawCount',
  'stickerCount',
  'drawVideoCount',
  'linkCount',
];

type ActivityEntry = [string, ChartList[number]['value']];

const orderEntriesByKeys = (
  entries: ActivityEntry[],
  sortKeyOrder: string[]
) => {
  if (!sortKeyOrder.length) return entries;

  const entryByKey = new Map(entries);
  const usedKeys = new Set<string>();
  const result: ActivityEntry[] = [];

  for (const countKey of sortKeyOrder) {
    const countValue = entryByKey.get(countKey);
    if (countValue !== undefined) {
      result.push([countKey, countValue]);
      usedKeys.add(countKey);
    }

    if (countKey.endsWith('Count')) {
      const listKey = `${countKey.slice(0, -'Count'.length)}List`;
      const listValue = entryByKey.get(listKey);
      if (listValue !== undefined) {
        result.push([listKey, listValue]);
        usedKeys.add(listKey);
      }
    }
  }

  const rest = entries.filter(([key]) => !usedKeys.has(key));
  return [...result, ...rest];
};

const mapDetailContent = (
  detailTitle: string | undefined,
  detail: ActivityDetail
) => {
  if (detailTitle === 'classtalkList' && detail.title) {
    return detail.title;
  }

  if (detailTitle === 'stickerList') {
    return STICKER;
  }

  if (detailTitle === 'drawList') {
    return DRAW_LIST_CONTENT_LABELS[detail.content] ?? detail.content;
  }

  return detail.content;
};

const GraphItem = ({ rate, idx, isAverage }: GraphItemProps) => {
  const cx = classNames.bind(styles);
  return (
    <div className={cx('graph_items', { graph_average: isAverage })}>
      <div className={cx('bar_graph_wrap')}>
        {isMobile || (
          <AnimatedBar
            width={rate}
            idx={idx}
            color={isAverage ? '#bdbdbd' : undefined}
          />
        )}
        <span className={cx('amount')}>{rate}</span>
      </div>
    </div>
  );
};

export default function LearningActivity({
  projectId,
  userId,
  tabIdx,
}: LearningActivity) {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();
  const tabList = [LEARNING_TOOL_ACTIVITY, CONTENT_LEARNING, QUIZ_LEARNING];

  const selectedIndexList = useAppSelector(selectProjectIndexList);
  const selectedActivityList = useAppSelector(
    userId ? selectStudentActivityList : selectMyProjectActivityList
  );

  const [expandedItemsByTab, setExpandedItemsByTab] = useState<
    Record<number, number | null>
  >({});
  const [activeTabIdx, setActiveTabIdx] = useState(() => tabIdx ?? 0);
  const [curIndexContent, setCurIndexContent] = useState(ALL);
  const [indexSeq, setIndexSeq] = useState(0);
  const [quizIndexSeq, setQuizIndexSeq] = useState(0);
  const [totalCorrectRate, setTotalCorrectRate] = useState('');
  const [activityList, setActivityList] = useState<ChartList | QuizChartList>(
    []
  );
  const [topThreeActivityValues, setTopThreeActivityValues] = useState<
    number[]
  >([]);
  const [xAxisMaxData, setXAxisMaxData] = useState(0);
  const [isShowQuizList, setIsShowQuizList] = useState(false);

  const [getAnnotation] =
    analysisApiSlice[
      userId ? 'useLazyGetClassAnnotationQuery' : 'useLazyGetAnnotationQuery'
    ]();
  const [getContentsActivityData] =
    analysisApiSlice[
      userId
        ? 'useLazyGetClassContentsActivityQuery'
        : 'useLazyGetContentsActivityQuery'
    ]();
  const [getQuizActivityData] =
    analysisApiSlice[
      userId
        ? 'useLazyGetClassQuizActivityQuery'
        : 'useLazyGetQuizActivityQuery'
    ]();
  const [getQuizActivityDetails] =
    analysisApiSlice[
      userId ? 'useLazyGetClassQuizDetailsQuery' : 'useLazyGetQuizDetailsQuery'
    ]();

  const setIndex = (value: string, i: number) => {
    setCurIndexContent(value);
    setIndexSeq(i ? selectedIndexList[i - 1].seq : 0);
  };

  const getActivityData = async () => {
    try {
      const { data } = await (
        activeTabIdx === 0
          ? getAnnotation
          : activeTabIdx === 1
            ? getContentsActivityData
            : getQuizActivityData
      )({
        projectId,
        userId,
        ...(indexSeq && { seq: indexSeq }),
      });

      if (!data) return;

      setActivityChart(
        activeTabIdx === 0
          ? data.annotation
          : activeTabIdx === 1
            ? data.content
            : data
      );

      setTotalCorrectRate(activeTabIdx === 2 ? data.totalCorrectRate : '');
    } catch (error) {
      console.error('Fail to get annotation', error);
    }
  };

  const setActivityChart = (
    activityData: Annotation | ContentsActivity | QuizActivity
  ) => {
    const list = getActivityList(activityData);
    setActivityList(list);
    dispatch(
      userId
        ? studentProjectActivityListChanged(list)
        : myProjectActivityListChanged(list)
    );

    if (activeTabIdx === 0 || activeTabIdx === 1) {
      const descendingList = [...(list as ChartList)]
        .filter(
          (el): el is { title: string; value: number } =>
            typeof el.value === 'number'
        )
        .sort((a, b) => b.value - a.value);
      const max = descendingList[0]?.value || 0;
      const thresholds = [100, 200, 300, 500, 1000];

      setTopThreeActivityValues(
        [...new Set(descendingList.map((el) => el.value))]
          .filter((value) => value > 0)
          .slice(0, 3)
      );
      setXAxisMaxData(thresholds.find((threshold) => max <= threshold) || 1000);
    }
  };

  const getActivityList = (
    data: Annotation | ContentsActivity | QuizActivity
  ) => {
    if (!data) return [];

    if ('quizList' in data) {
      return data.quizList;
    }

    const entries = Object.entries(data) as ActivityEntry[];
    const sortKeyOrder =
      activeTabIdx === 0
        ? Object.keys(LEARNING_TOOLS_ACTIVITIES)
        : activeTabIdx === 1
          ? Object.keys(CONTENTS_LEARNING_ACTIVITIES)
          : [];

    const orderedEntries = orderEntriesByKeys(entries, sortKeyOrder);

    let list: ChartList | QuizChartList = orderedEntries.map(
      ([title, value]) => ({
        title,
        value,
      })
    );

    return list;
  };

  const showQuizList = async (i: number) => {
    try {
      const { data } = await getQuizActivityDetails({
        projectId,
        userId,
        page: 0,
        size: 30,
        seq: selectedIndexList[i]?.seq,
      });

      if (!data) return;

      const filteredQuizList = data.quizList.filter(
        ({ contentType, draw }: Quiz) => contentType !== 9 || draw
      );

      dispatch(quizListChanged(filteredQuizList));
      dispatch(quizTotalCorrectChanged(data.totalCorrectRate));
      setQuizIndexSeq(selectedIndexList[i]?.seq);
      setIsShowQuizList(true);
      dispatch(quizListPageChanged(0));
    } catch (error) {
      console.error('Failed to get quiz activity data', error);
    }
  };

  useEffect(() => {
    setActiveTabIdx(tabIdx ?? 0);
    setCurIndexContent(ALL);
    setIndexSeq(0);
    setIsShowQuizList(false);
  }, [projectId, userId, tabIdx]);

  useEffect(() => {
    getActivityData();
    setIsShowQuizList(false);
  }, [activeTabIdx, projectId, userId]);

  useEffect(() => {
    getActivityData();
  }, [indexSeq]);

  useEffect(() => {
    if (!selectedActivityList.length) {
      getActivityData();
    }
  }, [selectedActivityList]);

  const activityMap = activeTabIdx
    ? CONTENTS_LEARNING_ACTIVITIES
    : LEARNING_TOOLS_ACTIVITIES;
  const detailKeys = activeTabIdx ? CONTENTS_WITH_DETAILS : TOOLS_WITH_DETAILS;

  return (
    <Tabs
      list={tabList}
      activeIdx={activeTabIdx}
      onChange={(i) => {
        setActiveTabIdx(i);
      }}
    >
      {activeTabIdx !== 2 && (
        <div className={cx('option_select')}>
          {selectedIndexList.length > 1 ? (
            <Select
              list={[ALL, ...selectedIndexList.map(({ content }) => content)]}
              value={curIndexContent}
              onClick={setIndex}
              name="learning-activity-group"
            />
          ) : (
            <span className={cx('index')}>{selectedIndexList[0]?.content}</span>
          )}
        </div>
      )}
      <div className={cx('tab_content', `tag_index_${activeTabIdx}`)}>
        {activeTabIdx === 2 ? (
          isShowQuizList ? (
            <QuizLearning
              projectId={projectId}
              userId={userId}
              indexSeq={quizIndexSeq}
              indexList={activityList as QuizChartList}
              hideQuizList={() => setIsShowQuizList(false)}
            />
          ) : (
            <div className={cx('quiz_total')}>
              <div className={cx('top')}>
                <div className={cx('desc')}>{SHOW_DETAILS_MESSAGE}</div>
                <TotalCorrectRate rate={totalCorrectRate} />
              </div>
              <div className={cx('graph_wrap')}>
                {(activityList as QuizChartList).map(
                  ({ content, correctRate, avgCorrectRate, message }, i) => (
                    <div
                      key={`${i}_${content}`}
                      className={cx('graph_list', {
                        disabled: message,
                      })}
                      onClick={() => showQuizList(i)}
                    >
                      <span className={cx('sub', 'text_clip')} title={content}>
                        {content}
                      </span>
                      <span className={cx('row')}>
                        {message ? (
                          <span className={cx('msg')}>{message}</span>
                        ) : (
                          <>
                            <GraphItem rate={correctRate} idx={i} />
                            <GraphItem
                              rate={avgCorrectRate}
                              idx={i}
                              isAverage
                            />
                          </>
                        )}
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>
          )
        ) : (
          <div className={cx('graph_wrap', `activeTabIdx_${activeTabIdx}`)}>
            {(activityList as ChartList).map(({ title, value }, index) => {
              if (typeof value !== 'number') {
                return null;
              }

              const percentage =
                xAxisMaxData > 0
                  ? value > xAxisMaxData
                    ? 100
                    : (value / xAxisMaxData) * 100
                  : 0;
              const rankIndex = topThreeActivityValues.indexOf(value);
              const isFirstRank = rankIndex === 0;
              const isSecondRank = rankIndex === 1 || rankIndex === 2;
              const activityTitle = activityMap[title] || '';
              const hasDetailInfo = detailKeys.includes(title);
              const activityValueLabel = `${
                value > MAX_ACTIVITY_X_AXIS ? `${MAX_ACTIVITY_X_AXIS}+` : value
              }${CASES}`;
              const detailItem = (activityList as ChartList)[index + 1];
              const detailTitle = detailItem?.title;
              const detailList = detailItem?.value;

              return (
                <div
                  key={`${title}-${index}`}
                  className={cx('graph_list', {
                    active: expandedItemsByTab[activeTabIdx] === index,
                  })}
                  style={{ '--index': index } as React.CSSProperties}
                >
                  <span
                    className={cx('sub', 'text_clip')}
                    title={activityTitle}
                  >
                    {activityTitle}
                  </span>
                  <div
                    className={cx('graph_items', {
                      color_fir: isFirstRank,
                      color_sec: isSecondRank,
                    })}
                  >
                    <TooltipWrapper
                      content={activityValueLabel}
                      placement="top"
                      followCursor={true}
                    >
                      {(tooltipProps) => (
                        <div className={cx('bar_graph_wrap')} {...tooltipProps}>
                          {isMobile ? (
                            <span
                              className={cx('amount', {
                                color_fir: isFirstRank,
                                color_sec: isSecondRank,
                              })}
                            >
                              {activityValueLabel}
                            </span>
                          ) : (
                            <span
                              className={cx('bar_graph')}
                              style={
                                {
                                  '--target-width': `${percentage}%`,
                                } as React.CSSProperties
                              }
                            />
                          )}
                        </div>
                      )}
                    </TooltipWrapper>
                    {expandedItemsByTab[activeTabIdx] === index && (
                      <ul className={cx('inner_info')}>
                        {Array.isArray(detailList)
                          ? detailList.map(
                              (
                                { page, regDate, content, existYn, title },
                                index
                              ) => (
                                <li
                                  key={`${page}-${regDate}-${content}-${index}`}
                                  className={cx({ disabled: existYn === 0 })}
                                >
                                  <div className={cx('inner_info_count')}>
                                    <span
                                      role="button"
                                      aria-disabled={existYn === 0}
                                      tabIndex={existYn === 0 ? -1 : 0}
                                      onClick={
                                        existYn === 0
                                          ? undefined
                                          : () =>
                                              openSelpubCloud(
                                                'viewer',
                                                projectId,
                                                false,
                                                '1'
                                              )
                                      }
                                    >
                                      {page}
                                      {isMobile ? 'p' : PAGE}
                                    </span>
                                  </div>
                                  {isMobile || (
                                    <>
                                      <div className={cx('inner_info_text')}>
                                        {existYn === 0
                                          ? '-'
                                          : mapDetailContent(detailTitle, {
                                              page,
                                              regDate,
                                              content,
                                              existYn,
                                              title,
                                            })}
                                      </div>
                                      <span className={cx('inner_info_date')}>
                                        {regDate}
                                      </span>
                                    </>
                                  )}
                                </li>
                              )
                            )
                          : null}
                      </ul>
                    )}
                  </div>
                  {hasDetailInfo &&
                  detailList &&
                  (detailList as ActivityDetail[]).length ? (
                    <button
                      type="button"
                      className={cx('btn', 'more_info', {
                        active: expandedItemsByTab[activeTabIdx] === index,
                      })}
                      onClick={() =>
                        setExpandedItemsByTab((prev) => ({
                          ...prev,
                          [activeTabIdx]:
                            prev[activeTabIdx] === index ? null : index,
                        }))
                      }
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 9L12 15L18 9"
                          stroke="#757575"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Tabs>
  );
}
